import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { useAuth } from "@/contexts/AuthContext";
import {
  PencilSimpleIcon, UserIcon, MapPinIcon, PhoneIcon, ArrowsClockwiseIcon, IdentificationBadgeIcon,
} from "@phosphor-icons/react";

/**
 * Representantes Técnicos Comerciales (RTC).
 * Origen único: Recursos Humanos. Un empleado con el puesto "Representante Técnico Comercial (RTC)"
 * aparece aquí y en todos los selectores automáticamente; si deja el puesto o no está activo pasa a
 * "No disponibles" conservando su historial. Aquí solo se ajusta el % GRATIVA por defecto.
 */
export default function Representatives() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = ["admin", "accountant"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [prueba, setPrueba] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        api.get("/representatives", { params: { activo: true } }),
        api.get("/representatives", { params: { activo: false } }),
      ]);
      setItems(a.data || []);
      setHistorico(h.data || []);
      if (isAdmin) setPrueba((await api.get("/representatives/prueba").catch(() => ({ data: [] }))).data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Registros de prueba: se eliminan definitivamente solo si no tienen ningún registro vinculado (lo valida el servidor)
  const sinVinculos = prueba.filter((r) => !Object.keys(r.referencias || {}).length);
  const eliminarPrueba = async (lista) => {
    if (!lista.length) return;
    if (!window.confirm(`¿Eliminar definitivamente ${lista.length === 1 ? `el registro de prueba ${lista[0].codigo}` : `${lista.length} registros de prueba`}?

Solo se eliminan los que no tienen ningún registro vinculado. No se puede deshacer.`)) return;
    let ok = 0;
    for (const r of lista) {
      try { await api.delete(`/representatives/prueba/${r.id}`); ok++; }
      catch (e) { toast.error(`${r.codigo}: ${apiErr(e)}`); }
    }
    if (ok) toast.success(`${ok} registro(s) de prueba eliminado(s)`);
    load();
  };

  const resync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/representatives/sync-from-employees");
      toast.success(`Sincronizado con Recursos Humanos: ${data.sincronizados} empleado(s) revisado(s).`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSyncing(false); }
  };

  const cm = useModalForm({ value: editing || undefined, abierto: Boolean(editing), onClose: () => setEditing(null), onSave: () => saveComision() });
  const saveComision = async () => {
    try {
      await api.put(`/representatives/${editing.id}`, { porcentaje_comision_default: Number(editing.porcentaje_comision_default || 0) });
      toast.success("GRATIVA actualizada");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout
      title="Representantes (RTC)"
      subtitle={`${items.length} representante${items.length === 1 ? "" : "s"} técnico${items.length === 1 ? "" : "s"} comercial${items.length === 1 ? "" : "es"} disponible${items.length === 1 ? "" : "s"}`}
      actions={
        <div className="flex gap-2">
          {isAdmin && (
            <button data-testid="sync-rtc-btn" onClick={resync} disabled={syncing}
              className="border border-border text-text-secondary hover:text-agro-green font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
              <ArrowsClockwiseIcon size={16} weight="bold" className={syncing ? "animate-spin" : ""} /> {syncing ? "Revisando…" : "Revisar con RRHH"}
            </button>
          )}
          <Link to="/rrhh" data-testid="rtc-go-hr"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <IdentificationBadgeIcon size={16} weight="bold" /> Registrar en Recursos Humanos
          </Link>
        </div>
      }
    >
      <div className="bg-agro-green/5 border border-agro-green/20 rounded-md p-3 mb-4 text-xs text-text-secondary" data-testid="rtc-origen">
        Los RTC se administran en <strong>Recursos Humanos</strong>: al registrar un empleado con el puesto
        <strong> Representante Técnico Comercial (RTC)</strong> aparece automáticamente aquí y en clientes, facturación,
        cobros, GRATIVA, viáticos y reportes. Sus datos, puesto y estado se mantienen sincronizados con su expediente.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <div className="col-span-3 p-8 text-center text-text-muted">Cargando…</div>
          : items.length === 0 ? (
            <div className="col-span-3 p-12 text-center text-text-muted bg-card border border-border/60 rounded-lg" data-testid="rtc-empty">
              <UserIcon size={32} className="inline mb-2 opacity-40" />
              <div className="text-text-primary font-medium">Aún no hay Representantes Técnicos Comerciales registrados</div>
              <div className="text-xs mt-1">Registra al empleado en Recursos Humanos con el puesto “Representante Técnico Comercial (RTC)”
                y aparecerá aquí automáticamente.</div>
              <Link to="/rrhh" className="inline-block mt-3 text-xs text-agro-green hover:underline">Ir a Recursos Humanos</Link>
            </div>
          ) : items.map((r) => <RtcCard key={r.id} r={r} onEdit={puedeEditar ? () => setEditing({ ...r }) : null} />)}
      </div>

      {historico.length > 0 && (
        <div className="mt-8" data-testid="rtc-historico">
          <h3 className="font-heading text-sm mb-1">No disponibles para nuevas asignaciones</h3>
          <div className="text-[11px] text-text-muted mb-3">Dejaron el puesto RTC o no están activos en Recursos Humanos. Se conservan su historial y sus relaciones (clientes, ventas, GRATIVA).</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
            {historico.map((r) => <RtcCard key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {isAdmin && prueba.length > 0 && (
        <details className="mt-8 bg-card border border-border/60 rounded-lg p-4" data-testid="rtc-prueba">
          <summary className="cursor-pointer text-sm font-heading">Registros de prueba excluidos de la operación ({prueba.length})</summary>
          <div className="flex items-start justify-between gap-4 mt-2 mb-3">
            <div className="text-[11px] text-text-muted">
              No corresponden a empleados de Recursos Humanos ni aparecen en selectores. Los que no tienen registros
              vinculados se pueden eliminar; los que sí tienen se conservan para no alterar el historial.
            </div>
            {sinVinculos.length > 0 && (
              <button onClick={() => eliminarPrueba(sinVinculos)} data-testid="rtc-prueba-eliminar-todos"
                className="shrink-0 text-xs px-3 py-1.5 border border-red-500/40 text-red-400 rounded-md hover:bg-red-500/10">
                Eliminar los {sinVinculos.length} sin vínculos</button>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-text-muted"><tr>
              <th className="p-2 text-left">Código</th><th className="p-2 text-left">Nombre</th><th className="p-2 text-left">Registros vinculados</th><th className="p-2"></th></tr></thead>
            <tbody>
              {prueba.map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="p-2 font-mono">{r.codigo}</td><td className="p-2">{r.nombre}</td>
                  <td className="p-2 text-text-muted">{Object.entries(r.referencias || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "Ninguno"}</td>
                  <td className="p-2 text-right">
                    {!Object.keys(r.referencias || {}).length && (
                      <button onClick={() => eliminarPrueba([r])} data-testid={`rtc-prueba-eliminar-${r.codigo}`}
                        className="text-[11px] px-2 py-1 border border-border rounded text-text-muted hover:text-red-400 hover:border-red-400/40">Eliminar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...cm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-sm" data-testid="rtc-modal">
            <div className="p-5 border-b border-border flex justify-between">
              <h3 className="font-heading text-lg font-semibold">GRATIVA de {editing.nombre}</h3>
              <BotonCerrar onClick={cm.cerrar} />
            </div>
            <div className="p-5 space-y-3">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium block">% GRATIVA por defecto (Gratificación Variable)</label>
              <input type="number" min={0} max={100} step="0.1" value={editing.porcentaje_comision_default ?? 0} data-testid="rtc-comision"
                onChange={(e) => setEditing({ ...editing, porcentaje_comision_default: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              <div className="text-[11px] text-text-muted">Nombre, contacto, región, puesto y estado se editan en el expediente de Recursos Humanos.</div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={cm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button onClick={cm.guardar} disabled={cm.saving} data-testid="rtc-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md disabled:opacity-40">{cm.saving ? "Guardando…" : "Guardar"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function RtcCard({ r, onEdit }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5" data-testid={`rtc-card-${r.codigo}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-agro-green to-agro-yellow flex items-center justify-center text-black font-semibold flex-shrink-0">
            {(r.nombre || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-mono text-xs text-agro-green">{r.codigo}</div>
            <div className="font-heading font-semibold text-text-primary truncate">{r.nombre}</div>
            <div className="text-[10px] text-text-muted">Empleado {r.empleado_codigo || "—"} · {r.puesto || "RTC"}</div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase text-text-muted">GRATIVA</div>
          <div className="text-sm font-semibold text-agro-yellow">{r.porcentaje_comision_default || 0}%</div>
        </div>
      </div>
      <div className="text-xs text-text-secondary space-y-1 pt-3 border-t border-border/40">
        {r.region && <div className="flex items-center gap-1.5"><MapPinIcon size={12} /> {r.region}</div>}
        {r.telefono && <div className="flex items-center gap-1.5"><PhoneIcon size={12} /> {r.telefono}</div>}
        {r.email && <div className="truncate">{r.email}</div>}
        {r.motivo_no_disponible && <div className="text-agro-yellow">{r.motivo_no_disponible}</div>}
      </div>
      {onEdit && (
        <button onClick={onEdit} data-testid={`edit-rtc-${r.codigo}`}
          className="w-full mt-3 text-xs py-1.5 border border-border rounded hover:border-agro-green/40 text-text-secondary flex items-center justify-center gap-1.5">
          <PencilSimpleIcon size={12} /> Ajustar GRATIVA
        </button>
      )}
    </div>
  );
}
